import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, Calendar, Tag } from 'lucide-react';

const Blog = () => {
  const { t, language } = useLanguage();
  const posts = t('blog.posts') || [];

  const labels = {
    en: { insights: 'Insights', viewAll: 'View all articles', readArticle: 'Read article' },
    tr: { insights: 'Yayınlar', viewAll: 'Tüm yazıları görüntüle', readArticle: 'Devamını oku' },
    es: { insights: 'Artículos', viewAll: 'Ver todos los artículos', readArticle: 'Leer artículo' },
    fr: { insights: 'Articles', viewAll: 'Voir tous les articles', readArticle: 'Lire l\'article' },
  };

  const currentLabels = labels[language] || labels.en;

  const categoryColors = {
    'Intellectual Property': 'bg-blue-100 text-blue-700',
    'Real Estate': 'bg-green-100 text-green-700',
    'Corporate Law': 'bg-purple-100 text-purple-700',
    'Fikri Mülkiyet': 'bg-blue-100 text-blue-700',
    'Gayrimenkul': 'bg-green-100 text-green-700',
    'Şirketler Hukuku': 'bg-purple-100 text-purple-700',
    'Propiedad Intelectual': 'bg-blue-100 text-blue-700',
    'Inmobiliario': 'bg-green-100 text-green-700',
    'Derecho Corporativo': 'bg-purple-100 text-purple-700',
    'Propriété Intellectuelle': 'bg-blue-100 text-blue-700',
    'Immobilier': 'bg-green-100 text-green-700',
    'Droit des Sociétés': 'bg-purple-100 text-purple-700',
  };

  return (
    <section id="blog" className="relative bg-white py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6">
          <div>
            <span className="text-[#1e3a5f] font-semibold text-sm uppercase tracking-wider">{currentLabels.insights}</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1e3a5f] mt-4 mb-4" style={{ fontFamily: "'Cardo', Georgia, serif" }}>
              {t('blog.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-xl">
              {t('blog.subtitle')}
            </p>
          </div>
          <button className="flex items-center gap-2 text-[#1e3a5f] font-medium hover:gap-3 transition-all">
            {currentLabels.viewAll}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Blog Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {posts.map((post, index) => (
            <article
              key={post.id}
              className="group bg-white rounded-2xl border border-gray-200 hover:border-[#1e3a5f]/30 hover:shadow-xl transition-all duration-300 overflow-hidden"
            >
              {/* Image Placeholder */}
              <div className="h-48 bg-gradient-to-br from-[#f5f7fa] to-[#e8eef4] relative">
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-6xl font-bold text-[#1e3a5f]/10">0{index + 1}</span>
                </div>
                {/* Category Badge */}
                <div className="absolute top-4 left-4">
                  <span className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full ${categoryColors[post.category] || 'bg-gray-100 text-gray-700'}`}>
                    <Tag className="w-3 h-3" />
                    {post.category}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Date */}
                <div className="flex items-center gap-2 text-gray-400 text-sm mb-4">
                  <Calendar className="w-4 h-4" />
                  {post.date}
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-[#1e3a5f] mb-3 group-hover:text-[#3d5a80] transition-colors" style={{ fontFamily: "'Cardo', Georgia, serif" }}>
                  {post.title}
                </h3>

                {/* Excerpt */}
                <p className="text-gray-600 text-sm leading-relaxed mb-6">
                  {post.excerpt}
                </p>

                {/* Read More */}
                <button className="flex items-center gap-2 text-sm text-[#1e3a5f] font-medium group-hover:gap-3 transition-all">
                  {currentLabels.readArticle}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Blog;
